/**
 * PATH: packages/email-hub/src/feature/components/email-context/emailContextFormat.ts
 * FILE_ID: EMAILHUB-EMAILCONTEXT-FORMAT-001
 * PURPOSE: Pure derivations shared by the job-context rail and the header chips.
 * WHY: The related-email information now has two presentations (the rail block
 *      beside a wide reader, the chip popover at tablet width and below). These
 *      helpers are the part that must not fork — a chip that decided "there is
 *      a synced mailbox" differently from the rail would show an empty panel.
 * FLOW:
 * ┌──────────────┐   ┌────────────────────┐   ┌────────────────────────┐
 * │ raw email     │ → │ resolve / format   │ → │ label + initials, dates │
 * └──────────────┘   └────────────────────┘   └────────────────────────┘
 * RELATED:
 * - ./useJobSiblingEmails.ts: the sibling query that pairs with these.
 * - ../EmailJobContextRail.tsx: rail consumer.
 * - ./EmailContextChips.tsx: header-chip consumer.
 */
import type { Email } from '../../hooks/useEmails'

export interface SyncedMailbox {
  /** Operator-facing mailbox owner ("Tim Brand" or the address). */
  label: string
  /** Two-letter badge for the owner. */
  initials: string
  /** The address, only when it adds something the label did not say. */
  address: string | null
}

/** Initials from a free-form name, falling back to the address local part. */
export function getInitialsFromNameOrEmail(
  name: string | null | undefined,
  email: string | null | undefined,
): string {
  const trimmed = (name ?? '').trim()
  if (trimmed) {
    return trimmed
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  const local = (email ?? '').split('@')[0] ?? ''
  return local.slice(0, 2).toUpperCase() || '?'
}

/** Short list-style date ("12 May"); empty string for unparseable input. */
export function formatShortDate(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

/**
 * Resolve the mailbox this email was synced from.
 *
 * Input: an email row. Output: the mailbox identity, or null when the row
 * carries no attribution at all (legacy rows predating mailbox stamping) —
 * null is the signal for every consumer to render nothing rather than an
 * empty "Synced from" heading.
 */
export function resolveSyncedMailbox(email: Email): SyncedMailbox | null {
  const label =
    email.synced_by_name?.trim() || email.synced_by_email?.trim() || ''
  if (!label) return null
  const address = email.synced_by_email?.trim() || null
  return {
    label,
    initials: getInitialsFromNameOrEmail(email.synced_by_name, email.synced_by_email),
    // Only useful when the label was a name; otherwise it repeats the label.
    address: email.synced_by_name?.trim() && address ? address : null,
  }
}
